import React, { useState, useRef, useEffect } from 'react';
import { Plus, MoreHorizontal } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

const PageHeader = ({
  title,
  subtitle,
  onAdd,
  addDisabled,
  addLabel = 'Add',
  menuItems = [],
  showAdd = true,
  className
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={cn(
      "sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/40",
      className
    )}>
      <div className="flex items-center justify-between px-4 py-3 min-h-[56px]">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold truncate">{title}</h1>
          {subtitle && (
            <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
          )}
        </div>

        <div className="flex items-center gap-1 ml-3 shrink-0">
          {showAdd && onAdd && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onAdd}
              disabled={addDisabled}
              className="h-9 w-9 rounded-full"
              title={addLabel}
            >
              <Plus className="h-5 w-5" />
            </Button>
          )}

          {menuItems.length > 0 && (
            <div className="relative" ref={menuRef}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMenuOpen(!menuOpen)}
                className="h-9 w-9 rounded-full"
              >
                <MoreHorizontal className="h-5 w-5" />
              </Button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-background border border-border rounded-xl shadow-lg py-1 animate-in fade-in-0 zoom-in-95 duration-100">
                  {menuItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.onClick?.();
                        setMenuOpen(false);
                      }}
                      disabled={item.disabled}
                      className={cn(
                        "w-full px-4 py-2.5 text-left text-sm flex items-center gap-3",
                        "hover:bg-accent transition-colors",
                        item.destructive && "text-destructive",
                        item.disabled && "opacity-50 pointer-events-none"
                      )}
                    >
                      {item.icon && <item.icon className="h-4 w-4" />}
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
